import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import ICONS from '../../constants/icons';
import ChatContext from '../../services/ChatContext';
import ChatManager from '../../services/ChatManager';
import URLS from '../../constants/urls';
import CustomButton from "../../components/button/CustomButton"
import {
  CardContainer,
  CardHeader,
  CardHeaderIconCell,
  HeaderProfileCell,
  ProfilePic,
  MessengerIcon,
  ParticipantCardBody,
  CardName,
  CardLevel,
  ParticipantCardFooter,
  ParticipantHealthCell,
  ParticipantHealthCellValue,
  ParticipantPhysicsWrapper,
  ParticipantPhysic,
  CardFooter,
  FooterLine,
  MessengerIconImg,
  ButtonsWrapper,
  ButtonWrapper
} from './components';

const ParticipantCard = ({ participant, handleCancel, handleOpenMoreInfo }) => {
  const history = useHistory();

  const { conversations, setCurrentConversation } = useContext(ChatContext);

  const hasUnread = useMemo(() => {
    // Conversations are keyed by conversation uid, not user uid — find the direct
    // conversation with this participant by matching the OTHER participant's id.
    const uid = participant?.user?.uid;
    const conversation = Object.values(conversations || {}).find(
      (c) => !c.isGroup && c.user?.id === uid,
    );
    return !!(conversation?.unreadMessageCount || 0);
  }, [conversations, participant]);

  // Open a real 1:1 with this participant (resolve/create it on the backend so it
  // is sendable — no client-only draft that duplicates the row and can't send).
  const openDirectChat = async () => {
    const withUserUid = participant?.user?.uid;
    if (!withUserUid) return;
    try {
      const convo = await ChatManager.getInstance().startConversation(withUserUid);
      // Set it for an immediate render, but pass the id in the URL too: the page we
      // are leaving clears currentConversation in its unmount cleanup, which runs
      // after this handler, so the query param is what actually survives the jump.
      setCurrentConversation(convo);
      history.push(`${URLS.messagesV2}?conversation=${convo.groupId}`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('open participant chat failed', e);
    }
  };

  return (
    <CardContainer
      style={{
        minWidth: '24rem',
        width: 'max(26rem, Calc(100% / 6 - 2.4rem))',
        height: 'fit-content',
      }}
    >
      <CardHeader style={{ background: '#fafafa' }}>
        <CardHeaderIconCell>
          <MessengerIcon active={'message'} onClick={handleCancel}>
            <MessengerIconImg cancelBtn active={hasUnread} />
          </MessengerIcon>
        </CardHeaderIconCell>
        <HeaderProfileCell>
          <ProfilePic profilePic={participant?.avatar?.url || ''}>
            {!participant?.avatar?.url &&
              participant?.fullName
                ?.split(' ')
                .map((x) => x.substring(0, 1))
                .join('')}
          </ProfilePic>
        </HeaderProfileCell>
        <CardHeaderIconCell>
          <MessengerIcon active={'message'} onClick={openDirectChat}>
            <MessengerIconImg active={hasUnread} />
          </MessengerIcon>
        </CardHeaderIconCell>
      </CardHeader>
      <ParticipantCardBody>
        <CardName
          style={{
            marginBottom: '4px',
            color: '#373333dd',
            height: '4rem',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {participant?.fullName}
        </CardName>
        <CardLevel style={{ marginBottom: '1.2rem' }}>
          {participant?.programs?.[0]?.fitnessLevel}
        </CardLevel>
        <ParticipantPhysicsWrapper>
          <ParticipantPhysic
            physIcon={ICONS.Weight}
          >{`${participant?.weight}kg`}</ParticipantPhysic>
          <ParticipantPhysic physIcon={ICONS.Height}>{participant?.height}</ParticipantPhysic>
          <ParticipantPhysic physIcon={ICONS.Age}>{`${participant?.age}y`}</ParticipantPhysic>
        </ParticipantPhysicsWrapper>
      </ParticipantCardBody>
      <ParticipantCardFooter>
        <ParticipantHealthCell>
          Health Status
          <ParticipantHealthCellValue>
            {(participant?.programs?.[0]?.healthCondition &&
              participant?.programs?.[0]?.healthConditionDetails) ||
              'Healthy'}
          </ParticipantHealthCellValue>
        </ParticipantHealthCell>
        <ParticipantHealthCell>
          Injuries
          <ParticipantHealthCellValue>
            {`${
              participant?.programs?.[0]?.injuryDetails ||
              participant?.programs?.[0]?.injury ||
              'None'
            }`}
          </ParticipantHealthCellValue>
        </ParticipantHealthCell>
      </ParticipantCardFooter>
      <CardFooter style={{ height: '5.8rem' }}>
        <ButtonsWrapper>
          <ButtonWrapper>
            <CustomButton
              outlined
              borderRadius="8px"
              hoverBackground="#00ff91"
              backgroundColor="#ffffff"
              style={{ minHeight: '3.6rem' }}
              fontSize="1.4rem"
              height="3.6rem"
              onClick={ handleOpenMoreInfo }
            >
              More Info
            </CustomButton>
          </ButtonWrapper>
        </ButtonsWrapper>
      </CardFooter>
      <CardFooter style={{ height: '9px', background: '#fafafa' }}>
        <FooterLine />
        <FooterLine />
      </CardFooter>
    </CardContainer>
  );
};

export default ParticipantCard;
